import { jsx, jsxs } from "react/jsx-runtime";
import { Text, Columns, Column, IconChevron, Box } from "braid-design-system";
import { createContext, useContext, useState, useEffect } from "react";
import { callToAction, fullWidth, stickyAction, accordionClosed, dropdown } from "./Accordion.css.mjs";
const initialState = {
  position: "header"
};
const CommonContext = createContext(initialState);
const useCommonContext = () => useContext(CommonContext);
const CommonContextProvider = ({
  position,
  children
}) => {
  const value = { position };
  return /* @__PURE__ */ jsx(CommonContext.Provider, { value, children });
};
const HEADER_FOOTER_LINK_CONTEXT_KEY = "linkContextHeaderFooter";
const DATA_HEADER_FOOTER_LINK_CONTEXT_KEY = `data-${HEADER_FOOTER_LINK_CONTEXT_KEY}`;
const mapLinkActionHelper = (direction, {
  key,
  opening,
  closing
}) => ({
  [key]: direction === "opening" ? opening : closing
});
const sendLinkEventDefault = ({
  linkContext: _linkContext
}) => {
};
const LinkProviderContext = createContext({
  sendLinkEvent: sendLinkEventDefault
});
const LinkProvider = LinkProviderContext.Provider;
const useLink = () => {
  const { sendLinkEvent } = useContext(LinkProviderContext);
  const { position } = useCommonContext();
  const isLinkProviderReady = sendLinkEvent !== sendLinkEventDefault;
  return {
    sendLinkEvent: ({ linkContext }) => sendLinkEvent({ linkContext: { ...linkContext, position } }),
    injectLinkContextToData: (linkContext, data) => {
      const linkContextHeaderFooter = {
        ...linkContext,
        position
      };
      return isLinkProviderReady ? {
        ...data,
        [HEADER_FOOTER_LINK_CONTEXT_KEY]: linkContextHeaderFooter
      } : data;
    }
  };
};
const isHeaderFooterLink = (arg) => typeof arg === "object";
const linkContextDestructor = (props, mapperFn) => {
  const { [DATA_HEADER_FOOTER_LINK_CONTEXT_KEY]: hfProps, ...restProps } = props;
  if (isHeaderFooterLink(hfProps)) {
    return [
      // although we know `restProps` is `T`, TS still not able to infer the type correctly, so we need to cast it.
      restProps,
      (mapperFn == null ? void 0 : mapperFn(hfProps)) || // although we know `hfProps` is `S`, TS still not able to infer the type correctly, so we need to cast it.
      hfProps
    ];
  }
  return [props];
};
const ChevronIconWrapper = ({
  icon: Icon,
  label,
  size = "standard",
  tone = "neutral",
  upDirectionCondition
}) => /* @__PURE__ */ jsx(Text, { size, tone, children: /* @__PURE__ */ jsxs(Columns, { space: "xxsmall", alignY: "center", children: [
  Icon ? /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Icon, { alignY: "lowercase" }) }) : null,
  label ? /* @__PURE__ */ jsx(Column, { width: "content", children: label }) : null,
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
    IconChevron,
    {
      alignY: "lowercase",
      direction: upDirectionCondition ? "up" : "down"
    }
  ) })
] }) });
const Accordion = ({
  callToAction: callToAction$1,
  children,
  isInline = false,
  isDirectionUpward = false,
  isCallToActionFullWidth = false,
  onExpendCollapseLinkContextData,
  onOpen = () => null,
  actionDisplayText,
  actionTone = "neutral",
  isStickyAction = false
}) => {
  const { sendLinkEvent } = useLink();
  const [isOpen, setIsOpen] = useState(false);
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };
  useEffect(() => {
    if (isOpen) {
      onOpen();
    }
  }, [isOpen, onOpen]);
  const ActionBox = /* @__PURE__ */ jsxs(
    Box,
    {
      onClick: () => {
        handleToggle();
        sendLinkEvent({
          linkContext: {
            openingOrClosing: isOpen ? "closing" : "opening",
            ...onExpendCollapseLinkContextData
          }
        });
      },
      className: callToAction,
      "data-automation": onExpendCollapseLinkContextData.source,
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            marginRight: actionDisplayText ? "large" : "xxsmall",
            className: {
              [fullWidth]: isCallToActionFullWidth
            },
            children: callToAction$1(isOpen)
          }
        ),
        /* @__PURE__ */ jsx(Box, { className: { [stickyAction]: isStickyAction }, children: /* @__PURE__ */ jsx(
          ChevronIconWrapper,
          {
            label: actionDisplayText,
            size: "small",
            tone: actionTone,
            upDirectionCondition: isOpen
          }
        ) })
      ]
    }
  );
  const ContentBox = /* @__PURE__ */ jsx(
    Box,
    {
      className: {
        [accordionClosed]: !isOpen,
        [dropdown]: !isInline
      },
      children
    }
  );
  return /* @__PURE__ */ jsxs(
    Box,
    {
      display: "flex",
      flexDirection: isDirectionUpward ? "columnReverse" : "column",
      position: "relative",
      children: [
        ActionBox,
        ContentBox
      ]
    }
  );
};
export {
  Accordion,
  ChevronIconWrapper,
  CommonContextProvider,
  DATA_HEADER_FOOTER_LINK_CONTEXT_KEY,
  LinkProvider,
  linkContextDestructor,
  mapLinkActionHelper,
  useLink
};
